import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { HeartPulse, CalendarCheck, Users, Activity, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const CLINIC_IMG =
  "https://images.unsplash.com/photo-1777269749032-d8d458ae594d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwY2xpbmljJTIwbW9kZXJuJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg3NjE4ODI1fDA&ixlib=rb-4.1.0&q=85";

function Feature({ icon: Icon, title, text }) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 transition-all duration-200 hover:shadow-sm">
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-sky-50 text-[#0284C7]">
        <Icon className="h-5 w-5" strokeWidth={2.4} />
      </div>
      <h3 className="text-lg font-extrabold tracking-tight text-slate-900">{title}</h3>
      <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-500">{text}</p>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user.role === "doctor" || user.role === "assistant") navigate("/panel");
      else if (user.role === "admin") navigate("/admin");
      else navigate("/buscar");
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-20 border-b border-[#E2E8F0] bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0284C7] text-white">
              <HeartPulse className="h-5 w-5" strokeWidth={2.6} />
            </div>
            <span className="text-lg font-extrabold tracking-tight text-slate-900">SaludCitaRD</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" className="rounded-full font-bold text-slate-600" data-testid="nav-login-btn">
                Iniciar sesión
              </Button>
            </Link>
            <Link to="/registro">
              <Button className="rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="nav-register-btn">
                Crear cuenta
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-16 lg:grid-cols-2 lg:py-24">
        <div className="animate-fade-in-up">
          <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-bold text-[#0284C7]">
            Salud conectada · República Dominicana
          </span>
          <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Agenda con tu médico y sigue tu turno en vivo
          </h1>
          <p className="mt-5 max-w-md text-base font-semibold leading-relaxed text-slate-500">
            Encuentra especialistas, reserva por hora fija o por llegada, y mira en tiempo real
            cuántos pacientes tienes por delante.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/registro">
              <Button className="rounded-full bg-[#0284C7] px-6 font-bold hover:bg-[#0369a1]" data-testid="hero-cta-btn">
                Empezar ahora <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="rounded-full border-[#E2E8F0] px-6 font-bold" data-testid="hero-login-btn">
                Soy médico
              </Button>
            </Link>
          </div>
        </div>
        <div className="relative animate-fade-in-up">
          <div className="overflow-hidden rounded-2xl border border-[#E2E8F0]">
            <img src={CLINIC_IMG} alt="Clínica moderna" className="h-[420px] w-full object-cover" />
          </div>
          <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-sm sm:block">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Turno actual</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-extrabold text-[#0284C7]">A-12</span>
              <span className="text-xs font-bold text-emerald-500">● en vivo</span>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24">
        <div className="grid gap-6 md:grid-cols-3">
          <Feature icon={CalendarCheck} title="Dos modalidades" text="Reserva por hora fija o por llegada con número de turno secuencial." />
          <Feature icon={Activity} title="Turnos en vivo" text="Monitor en tiempo real con el turno actual y pacientes por delante." />
          <Feature icon={Users} title="Expediente digital" text="Historial en línea de tiempo con notas y archivos por cada paciente." />
        </div>
      </section>
    </div>
  );
}
